/*
  <ia-endless-scents> — toggles which scent family is active on mobile.
  Tabs, panels and dots are server-rendered (one set per _ia-scent-group block)
  and paired by a shared data-es-key; this component never builds DOM, it only
  flips .is-active / aria-selected. On desktop every panel is shown by CSS, so the
  toggling is a no-op there. On mobile a horizontal swipe moves to the next/previous
  family, like the tabs and dots — but a family's own scents row scrolls first, and
  only a swipe started at its end changes family. In the theme editor, selecting a family — or a
  scent inside one — reveals that family's panel.
  Desktop (optional, section settings): prev/next arrows that hide at their end of the
  row, and a progress thumb sized to the visible share of the row.
*/
import { onSwipe } from 'ia-util-swipe';

// Mobile shows one family at a time; desktop shows them all in a scrolling row,
// where a horizontal touch drag is the native scroll, not a family change.
const DESKTOP_MQ = window.matchMedia('(min-width: 1024px)');

// 1px tolerance so sub-pixel rounding doesn't read as "not at the edge".
function scrollEdges(row) {
  const max = row.scrollWidth - row.clientWidth;
  return { start: row.scrollLeft <= 1, end: row.scrollLeft >= max - 1 };
}

if (!customElements.get('ia-endless-scents')) {
  class IaEndlessScents extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-es-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-es-panel]'));
      this.dots = Array.from(this.querySelectorAll('[data-es-dot]'));
      this.initRowNav();
      if (!this.tabs.length) return;

      this.onControlClick = this.onControlClick.bind(this);
      this.tabs.forEach((tab) => tab.addEventListener('click', this.onControlClick));
      this.dots.forEach((dot) => dot.addEventListener('click', this.onControlClick));

      // Adopt the CSS default (first family) unless one is already marked active.
      const preselected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
      this.activate(preselected ? preselected.dataset.esKey : this.tabs[0].dataset.esKey);

      this.onTouchStart = this.onTouchStart.bind(this);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });

      this.unbindSwipe = onSwipe(this, {
        onNext: () => this.swipeEdges.end && this.step(1),
        onPrev: () => this.swipeEdges.start && this.step(-1),
        enabled: () => !DESKTOP_MQ.matches,
      });

      this.onBlockSelect = this.onBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.unbindSwipe?.();
      this.navController?.abort();
      this.navObserver?.disconnect();
    }

    // Desktop arrows / progress over the drag-scroll row; CSS keeps them off mobile,
    // where the track doesn't scroll and they stay hidden anyway.
    initRowNav() {
      this.track = this.querySelector('[data-es-track]');
      this.prevButton = this.querySelector('[data-es-prev]');
      this.nextButton = this.querySelector('[data-es-next]');
      this.progress = this.querySelector('[data-es-progress]');
      if (!this.track || !(this.prevButton || this.nextButton || this.progress)) return;

      this.navController = new AbortController();
      const { signal } = this.navController;
      this.prevButton?.addEventListener('click', () => this.scrollRow(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.scrollRow(1), { signal });
      this.track.addEventListener('scroll', () => this.syncRowNav(), { passive: true, signal });
      this.navObserver = new ResizeObserver(() => this.syncRowNav());
      this.navObserver.observe(this.track);
      this.syncRowNav();
    }

    syncRowNav() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const scrollable = scrollWidth - clientWidth > 1;
      const edges = scrollEdges(this.track);
      if (this.prevButton) this.prevButton.hidden = !scrollable || edges.start;
      if (this.nextButton) this.nextButton.hidden = !scrollable || edges.end;
      if (!this.progress) return;
      this.progress.hidden = !scrollable;
      // Thumb width = visible share; its offset is in thumb widths (translateX %).
      this.progress.style.setProperty('--ia-es-progress-size', (clientWidth / scrollWidth).toFixed(4));
      this.progress.style.setProperty('--ia-es-progress', (scrollLeft / clientWidth).toFixed(4));
    }

    // Brings the next family that's cut off at that side to the row's start.
    scrollRow(direction) {
      const view = this.track.getBoundingClientRect();
      const boxes = this.panels.map((panel) => panel.getBoundingClientRect());
      const target =
        direction > 0
          ? boxes.find((box) => box.left > view.left + 1 && box.right > view.right + 1)
          : boxes.findLast((box) => box.left < view.left - 1);
      const left = target ? target.left - view.left : direction * this.track.clientWidth;
      this.track.scrollBy({ left, behavior: 'smooth' });
    }

    // Snapshot the scents row's edges before the gesture scrolls it: a swipe only
    // changes family if the row was already at that end when the touch began.
    onTouchStart(event) {
      const row = event.target.closest('[data-es-scroller]');
      this.swipeEdges = row ? scrollEdges(row) : { start: true, end: true };
    }

    // Next/previous family in tab order, wrapping at both ends like the dots imply.
    step(direction) {
      const count = this.tabs.length;
      const current = this.tabs.findIndex((tab) => tab.dataset.esKey === this.activeKey);
      const key = this.tabs[(current + direction + count) % count]?.dataset.esKey;
      this.activate(key);
      // Continue from where the swipe left off: backwards opens the row at its end.
      const row = this.panels.find((panel) => panel.dataset.esKey === key)?.querySelector('[data-es-scroller]');
      if (row) row.scrollLeft = direction < 0 ? row.scrollWidth : 0;
    }

    onControlClick(event) {
      this.activate(event.currentTarget.dataset.esKey);
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      // A family block owns a tab; a scent block sits inside a panel.
      const tab = event.target.querySelector('[data-es-tab]');
      if (tab) {
        this.activate(tab.dataset.esKey);
        return;
      }
      const panel = event.target.closest('[data-es-panel]');
      if (panel) this.activate(panel.dataset.esKey);
    }

    activate(key) {
      if (key == null) return;
      this.activeKey = key;
      this.tabs.forEach((tab) => {
        const active = tab.dataset.esKey === key;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });
      this.panels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.esKey === key);
      });
      this.dots.forEach((dot) => {
        const active = dot.dataset.esKey === key;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    }
  }

  customElements.define('ia-endless-scents', IaEndlessScents);
}
